/**
 * Free Gift Popup Manager
 * 
 * This script manages the free gift popup behavior by:
 * 1. Preventing the popup from being permanently dismissed until a gift is selected
 * 2. Monitoring localStorage for gift selection completion
 * 3. Clearing the dismissal flag until the user actually selects a gift
 * 
 * HOW IT WORKS:
 * - When a user adds items to cart, the Shopify app sets nf_utk46586 = 1 to prevent popup
 * - This script immediately removes that flag if no gift has been selected yet
 * - Once the user selects a gift (nf_pof and et_nf_pof are created), it allows normal behavior
 * 
 * TESTING:
 * Open browser console and use these commands:
 * - window.freeGiftPopupManager.debugStatus() - Shows current state
 * - window.freeGiftPopupManager.simulateCartAdd() - Simulates adding to cart
 * - window.freeGiftPopupManager.simulateGiftSelection() - Simulates selecting a gift
 * - window.freeGiftPopupManager.reset() - Resets the manager state
 * 
 * MONITORED LOCALSTORAGE KEYS:
 * - nf_utk46586: Popup dismissal flag (cleared until gift selected)
 * - nf_pof: Gift selection data (indicates gift was chosen)
 * - et_nf_pof: Gift selection timestamp (indicates gift was chosen)
 */

class FreeGiftPopupManager {
  constructor() {

    this.dismissalKey = 'nf_utk46586';
    this.giftSelectedKey = 'nf_pof';
    this.giftTimestampKey = 'et_nf_pof';
    this.monitoringInterval = null;
    this.isGiftSelected = false;
    this.isAllGiftsSelected = false; // all required promos satisfied
    this.defaultOfferIds = new Set(['46586','49326']); // default required offers
    this.requiredOfferIds = new Set(this.defaultOfferIds);
    this.allDismissalKeys = new Set([...this.requiredOfferIds].map(id => 'nf_utk' + id));
    this.pendingUpsellModalClose = false;
    this.cartThreshold = 30000; // 300 lei in cents (300 * 100)
    this.originalSetItem = null; // Store original localStorage.setItem to avoid recursion
    this.isCheckingThreshold = false; // Flag to prevent recursive threshold checks
    this.lastThresholdCheck = 0; // Timestamp of last threshold check to throttle
    this.lastLogTime = 0; // Timestamp of last log to reduce console spam
    this.thresholdMet = null;
    this._appliedThresholdMet = null; // Last threshold state applied to localStorage
    this._skipCartJsonIntercept = false; // Skip fetch hook for our own cart.json reads
    this._lastPopupTriggerAt = 0;
    this._popupTriggerCooldownMs = 5000;
    this._minThresholdCheckGapMs = 3000;
    this._thresholdMonitorIntervalMs = 10000;
    
    this.init();
  }

  /**
   * Fetch cart.json without re-entering the global fetch interceptor.
   */
  async fetchCartJson() {
    this._skipCartJsonIntercept = true;
    try {
      const response = await fetch('/cart.json');
      return await response.json();
    } finally {
      this._skipCartJsonIntercept = false;
    }
  }

  init() {
    // Check if gift is already selected on page load
    this.checkGiftSelection();
    
    // Start monitoring localStorage changes
    this.startMonitoring();
    
    // Listen for cart add events to trigger popup monitoring
    this.setupCartEventListeners();
    
    // Override localStorage setItem to catch changes immediately
    this.overrideLocalStorage();
    
    // Prepare upsell click handling (defer modal closing until cart add occurs)
    this.setupUpsellClickHandling();

    // DocApp gift modal: când counter-ul e complet (1/1, 2/2…), închide popup-ul
    // (butonul × e ascuns prin CSS; închidem programatic.)
    this.setupDocappGiftModalCounterObserver();
    
    // Check cart threshold on page load
    this.checkCartThresholdAndUpdateFlags();
  }

  /**
   * Parsează textul din .docapp-gift-choice-modal-counter (ex. "1/1") și închide modala când selected === total.
   */
  setupDocappGiftModalCounterObserver() {
    this._docappGiftCounterSnapshot = '';
    this._docappGiftClosing = false;

    const tick = () => {
      this.maybeCloseDocappGiftModalWhenCounterComplete();
    };

    let debounce;
    const schedule = () => {
      clearTimeout(debounce);
      debounce = setTimeout(tick, 80);
    };

    const observer = new MutationObserver(schedule);
    observer.observe(document.documentElement, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    });

    tick();
    setInterval(() => {
      if (document.getElementById('docapp-gift-choice-modal-wrapper')) {
        tick();
      }
    }, 400);
  }

  maybeCloseDocappGiftModalWhenCounterComplete() {
    const wrapper = document.getElementById('docapp-gift-choice-modal-wrapper');
    if (!wrapper || this._docappGiftClosing) {
      if (!wrapper) {
        this._docappGiftCounterSnapshot = '';
      }
      return;
    }

    const hasVisibleClass = wrapper.classList.contains('docapp-visible');
    const offsetParent = wrapper.offsetParent;
    const rects = wrapper.getClientRects();
    const br = wrapper.getBoundingClientRect();
    const hasBox = br.width > 0 && br.height > 0;
    const visible =
      hasVisibleClass ||
      offsetParent !== null ||
      rects.length > 0 ||
      hasBox;
    if (!visible) {
      this._docappGiftCounterSnapshot = '';
      return;
    }

    const counter = wrapper.querySelector('.docapp-gift-choice-modal-counter');
    if (!counter) return;

    const text = (counter.textContent || '').replace(/\s/g, '');
    if (text === this._docappGiftCounterSnapshot) return;
    this._docappGiftCounterSnapshot = text;

    const match = text.match(/^(\d+)\/(\d+)$/);
    if (!match) return;

    const selected = parseInt(match[1], 10);
    const total = parseInt(match[2], 10);
    if (total <= 0 || selected !== total) return;

    this._docappGiftClosing = true;
    this.closeDocappGiftModal();
    setTimeout(() => {
      this._docappGiftClosing = false;
      this._docappGiftCounterSnapshot = '';
    }, 1500);
  }

  closeDocappGiftModal() {
    const fullscreen = document.querySelector(
      '.docapp-overlay-content.docapp-overlay-content__fullscreen'
    );

    const closeBtn = fullscreen
      ? fullscreen.querySelector('.docapp-overlay-close')
      : document.querySelector('.docapp-overlay-close');

    if (closeBtn) {
      try {
        const prevCss = closeBtn.style.cssText;
        closeBtn.style.cssText = `${prevCss};display:flex!important;visibility:visible!important;pointer-events:auto!important;opacity:1!important;position:relative!important;z-index:2147483646!important;`;
        closeBtn.click();
        closeBtn.style.cssText = prevCss;
      } catch (_) {
        /* continue */
      }
    }

    let node = document.getElementById('docapp-gift-choice-modal-wrapper');
    while (node && node !== document.body) {
      const cls = node.className;
      const classes =
        typeof cls === 'string'
          ? cls.split(/\s+/)
          : cls && typeof cls.baseVal === 'string'
            ? cls.baseVal.split(/\s+/)
            : [];
      if (classes.includes('docapp-overlay')) {
        node.style.setProperty('display', 'none', 'important');
        node.setAttribute('aria-hidden', 'true');
        try {
          document.body.style.overflow = '';
          document.body.classList.remove('docapp-no-scroll');
        } catch (_) {
          /* ignore */
        }
        return;
      }
      node = node.parentElement;
    }

    if (fullscreen && fullscreen.parentElement) {
      fullscreen.parentElement.style.setProperty('display', 'none', 'important');
    }
  }

  overrideLocalStorage() {
    // Store original setItem for use in other methods
    this.originalSetItem = localStorage.setItem;
    const self = this;
    
    localStorage.setItem = function(key, value) {
      // Call the original setItem first
      self.originalSetItem.apply(this, arguments);
      
      // Update required offers if nf_ses changes
      if (key === 'nf_ses') {
        setTimeout(() => { self.updateRequiredOffersFromSession(); }, 50);
      }
      // Check if any dismissal keys are set
      if (self.allDismissalKeys.has(key) && value === '1' && !self.isCheckingThreshold) {
        setTimeout(() => {
          self.checkGiftSelection();
          if (self.isAllGiftsSelected) return;

          // Use cached threshold when possible — avoid cart.json on every app dismissal write
          if (self.thresholdMet === true) {
            localStorage.removeItem(key);
            self.triggerPopupIfAvailable();
            return;
          }
          if (self.thresholdMet === false) return;

          self.checkCartThresholdAndUpdateFlags({ force: true });
        }, 50);
      }
      
      // Check if gift selection keys are being set
      if (key === self.giftSelectedKey || key === self.giftTimestampKey) {
        setTimeout(() => {
          self.checkGiftSelection();
        }, 100);
      }
    };
  }

  /**
   * Check cart threshold and update dismissal flags accordingly
   * This is called to proactively check if cart meets threshold
   */
  async checkCartThresholdAndUpdateFlags(options = {}) {
    const force = options.force === true;
    const now = Date.now();

    if (this.isCheckingThreshold) {
      return Promise.resolve();
    }
    if (!force && now - this.lastThresholdCheck < this._minThresholdCheckGapMs) {
      return Promise.resolve();
    }

    this.isCheckingThreshold = true;
    this.lastThresholdCheck = now;
    try {
      const cart = await this.fetchCartJson();
      const meetsThreshold = this.checkCartThreshold(cart);
      const previouslyMet = this.thresholdMet === true;
      const thresholdChanged = this._appliedThresholdMet !== meetsThreshold;
      this.thresholdMet = meetsThreshold;
      this.lastLogTime = now;

      if (!thresholdChanged) {
        return;
      }

      this._appliedThresholdMet = meetsThreshold;

      if (!meetsThreshold) {
        this.allDismissalKeys.forEach(key => {
          if (this.originalSetItem) {
            this.originalSetItem.call(localStorage, key, '1');
          } else {
            localStorage.setItem(key, '1');
          }
        });
        this.clearGiftSelectionData();
        return;
      }

      if (!this.isAllGiftsSelected) {
        this.allDismissalKeys.forEach(key => {
          localStorage.removeItem(key);
        });
        if (!previouslyMet) {
          console.log('CART CONDITIONS ARE MET');
        }
        this.triggerPopupIfAvailable();
      }
    } catch (error) {
      // Silent error handling
    } finally {
      this.isCheckingThreshold = false;
    }
  }

  checkGiftSelection() {
    const giftData = localStorage.getItem(this.giftSelectedKey);
    const giftTimestamp = localStorage.getItem(this.giftTimestampKey);
    
    this.isGiftSelected = !!(giftData && giftTimestamp);
    
    // Parse selected offers from nf_pof: { i: [ { o: <offerId> } ] }
    const selectedOffers = new Set();
    try {
      if (giftData) {
        const parsed = JSON.parse(giftData);
        const items = Array.isArray(parsed?.i) ? parsed.i : [];
        for (const it of items) {
          if (typeof it?.o !== 'undefined') selectedOffers.add(String(it.o));
        }
      }
    } catch(_) {}

    // Refresh required offers from nf_ses if available
    this.updateRequiredOffersFromSession();

    // Determine if all required offers are selected
    if (this.requiredOfferIds.size > 0) {
      this.isAllGiftsSelected = [...this.requiredOfferIds].every(id => selectedOffers.has(id));
    } else {
      // Fallback to original single-flag logic
      this.isAllGiftsSelected = this.isGiftSelected;
    }
  }

  clearGiftSelectionData() {
    try {
      localStorage.removeItem(this.giftSelectedKey);
      localStorage.removeItem(this.giftTimestampKey);
    } catch (_) {}
    this.isGiftSelected = false;
    this.isAllGiftsSelected = false;
    this.requiredOfferIds = new Set(this.defaultOfferIds);
    this.allDismissalKeys = new Set([...this.requiredOfferIds].map(id => 'nf_utk' + id));
  }

  startMonitoring() {
    // Monitor localStorage changes every 100ms
    this.monitoringInterval = setInterval(() => {
      this.monitorLocalStorage();
    }, 100);
  }

  stopMonitoring() {
    if (this.monitoringInterval) {
      clearInterval(this.monitoringInterval);
      this.monitoringInterval = null;
    }
  }

  monitorLocalStorage() {
    // Check if gift has been selected
    const giftData = localStorage.getItem(this.giftSelectedKey);
    const giftTimestamp = localStorage.getItem(this.giftTimestampKey);
    
    if (giftData && giftTimestamp && !this.isGiftSelected) {
      this.isGiftSelected = true;
      return;
    }

    // Periodic threshold sync — slow fallback only (cart events handle the hot path)
    if (!this.isAllGiftsSelected) {
      if (Date.now() - this.lastThresholdCheck > this._thresholdMonitorIntervalMs) {
        this.checkCartThresholdAndUpdateFlags();
      }
    }
  }

  triggerPopupIfAvailable() {
    const now = Date.now();
    if (now - this._lastPopupTriggerAt < this._popupTriggerCooldownMs) {
      return;
    }
    this._lastPopupTriggerAt = now;

    setTimeout(() => {
      document.dispatchEvent(new CustomEvent('freeGiftPopupTrigger'));
    }, 100);
  }

  setupCartEventListeners() {
    // Listen for successful cart additions
    document.addEventListener('cart:added', () => {
      this.handleCartAdd();
    });

    // Also monitor for fetch requests to cart add URL
    this.interceptCartAddRequests();
  }

  handleCartAdd() {
    setTimeout(() => {
      this.checkGiftSelection();
      this.checkCartThresholdAndUpdateFlags({ force: true });
    }, 100);
  }

  /**
   * Calculate cart total using discounted prices (after discounts)
   * @param {Object} cart - Cart object from cart.json
   * @returns {number} Total in cents using discounted prices
   */
  calculateDiscountedCartTotal(cart) {
    if (!cart || !cart.items) return 0;
    
    // Use items_subtotal_price which is the discounted total
    if (cart.items_subtotal_price) {
      return cart.items_subtotal_price;
    }
    
    // Fallback: calculate from items if items_subtotal_price not available
    let discountedTotal = 0;
    cart.items.forEach(item => {
      // Use final_line_price if available, otherwise fall back to final_price * quantity
      if (item.final_line_price) {
        discountedTotal += item.final_line_price;
      } else if (item.final_price) {
        discountedTotal += item.final_price * item.quantity;
      } else {
        // Fallback: use original price if no discounted price available
        discountedTotal += item.original_line_price || (item.original_price * item.quantity);
      }
    });
    
    return discountedTotal;
  }

  /**
   * Check if cart meets threshold using discounted prices
   * @param {Object} cart - Cart object from cart.json
   * @returns {boolean} True if discounted cart total >= threshold
   */
  checkCartThreshold(cart) {
    const discountedTotal = this.calculateDiscountedCartTotal(cart);
    return discountedTotal >= this.cartThreshold;
  }

  interceptCartAddRequests() {
    // Store original fetch function
    const originalFetch = window.fetch;
    const self = this;
    
    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      const url = args[0];
      
      if (typeof url === 'string' && url.includes('/cart/add')) {
        setTimeout(() => {
          self.handleCartAdd();
          if (self.pendingUpsellModalClose) {
            self.checkGiftSelection();
            if (self.isAllGiftsSelected) {
              self.closeTdfDialogModal();
              self.pendingUpsellModalClose = false;
            } else {
              self.triggerPopupIfAvailable();
            }
          }
        }, 300);
      }

      return response;
    };
  }

  /**
   * Intercept fetch requests to /cart.json to check original prices
   * and prevent popup if threshold not met
   * Note: This is now handled in interceptCartAddRequests to avoid double override
   */
  interceptCartJsonFetches() {
    // This method is kept for clarity but the logic is in interceptCartAddRequests
    // to avoid overriding window.fetch twice
  }

  setupUpsellClickHandling() {
    // Delegate click: mark intent, let the app handle add-to-cart first
    document.addEventListener('click', (event) => {
      const target = event.target;
      if (!target) return;
      const upsellButton = target.closest('[data-e="click_add_upsell_product"]');
      if (!upsellButton) return;

      // Mark that we should close the modal after the cart add completes
      this.pendingUpsellModalClose = true;

      // As a safety, if no cart add happens, close after a short delay only if done
      setTimeout(() => {
        if (this.pendingUpsellModalClose) {
          this.checkGiftSelection();
          if (this.isAllGiftsSelected) {
            this.closeTdfDialogModal();
            this.pendingUpsellModalClose = false;
          }
        }
      }, 3000);
    });
  }

  updateRequiredOffersFromSession() {
    try {
      const nf_ses = localStorage.getItem('nf_ses');
      if (!nf_ses) return;
      const parsed = JSON.parse(nf_ses);
      const items = Array.isArray(parsed?.i) ? parsed.i : [];
      const ids = new Set();
      for (const it of items) {
        if (typeof it?.o !== 'undefined') ids.add(String(it.o));
      }
      if (ids.size > 0) {
        this.requiredOfferIds = ids;
        // Also keep dismissal keys set in sync
        this.allDismissalKeys = new Set([...ids].map(id => 'nf_utk' + id));
      }
    } catch(_) {}
  }

  closeTdfDialogModal() {
    const modal = document.querySelector('#tdf_dialog_modal, .tdf_dialog_modal, [id="tdf_dialog_modal"], [data-modal-id="tdf_dialog_modal"]')
      || document.getElementById('tdf_dialog_modal');
    if (!modal) return;

    const performReload = () => {
      setTimeout(() => {
        try { window.location.reload(); } catch (_) {}
      }, 150);
    };

    try {
      if (typeof modal.close === 'function') {
        modal.close();
        performReload();
        return;
      }
    } catch (_) {}

    const closeEl = modal.querySelector('[data-action="close"], [data-element="close"], [aria-label="Close"], .close, .modal__close, .tdf-dialog__close, button[type="button"]');
    if (closeEl) {
      closeEl.click();
      performReload();
      return;
    }

    modal.setAttribute('aria-hidden', 'true');
    if (modal.classList) {
      modal.classList.remove('is-active', 'open', 'opened', 'active', 'visible', 'show');
    }
    if (modal.style) {
      modal.style.display = 'none';
      modal.style.visibility = 'hidden';
      modal.style.opacity = '0';
    }
    if (modal.hasAttribute && modal.hasAttribute('open')) {
      modal.removeAttribute('open');
    }
    performReload();
  }

  // Public method to manually trigger gift selection check
  checkForGiftSelection() {
    this.checkGiftSelection();
  }

  // Public method to reset the manager
  reset() {
    this.isGiftSelected = false;
    this.checkGiftSelection();
  }

  // Debug method to log current state
  debugStatus() {
    console.log('=== Free Gift Popup Manager Status ===');
    console.log('Gift Selected (any):', this.isGiftSelected);
    console.log('All Gifts Selected:', this.isAllGiftsSelected);
    console.log('Required Offers:', [...this.requiredOfferIds]);
    console.log('Dismissal Keys:', [...this.allDismissalKeys]);
    console.log('Dismissal Key Value:', localStorage.getItem(this.dismissalKey));
    console.log('Gift Data:', localStorage.getItem(this.giftSelectedKey));
    console.log('Gift Timestamp:', localStorage.getItem(this.giftTimestampKey));
    
    // Check cart and threshold
    this.fetchCartJson()
      .then(cart => {
        const discountedTotal = this.calculateDiscountedCartTotal(cart);
        let originalTotal = 0;
        if (cart.items) {
          cart.items.forEach(item => {
            originalTotal += item.original_line_price || (item.original_price * item.quantity);
          });
        }
        console.log('Cart Original Total:', originalTotal / 100 + ' lei');
        console.log('Cart Discounted Total:', discountedTotal / 100 + ' lei');
        console.log('Threshold:', this.cartThreshold / 100 + ' lei');
        console.log('Meets Threshold (based on discounted):', discountedTotal >= this.cartThreshold);
        console.log('=====================================');
      })
      .catch(() => {
        console.log('Failed to fetch cart for debug');
        console.log('=====================================');
      });
  }

  // Manual test method
  simulateCartAdd() {
    localStorage.setItem(this.dismissalKey, '1');
    this.handleCartAdd();
  }

  // Manual test method to simulate gift selection
  simulateGiftSelection() {
    localStorage.setItem(this.giftSelectedKey, '{"i":[{"p":7564910723316,"o":46586,"n":2}]}');
    localStorage.setItem(this.giftTimestampKey, Date.now().toString());
    this.checkGiftSelection();
  }

  // Cleanup method
  destroy() {
    this.stopMonitoring();
  }
}

// Initialize once (defer runs before DOMContentLoaded — avoid double init)
function initFreeGiftPopupManager() {
  if (window.freeGiftPopupManager) return;
  window.freeGiftPopupManager = new FreeGiftPopupManager();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initFreeGiftPopupManager);
} else {
  initFreeGiftPopupManager();
}

// Export for potential external use
window.FreeGiftPopupManager = FreeGiftPopupManager;
